import React, { useState, useRef, useEffect, useContext } from 'react';
import { 
    CCard, 
    CCardBody, 
    CButton, 
    CFormInput, 
    CFormTextarea, 
    CModal, 
    CModalHeader, 
    CModalTitle, 
    CModalBody, 
    CModalFooter,
    CSpinner,
    CAlert
} from '@coreui/react';
import { AuthUserContext } from '../../session';
import axios from 'axios';
import config from '../../../utils/EnvironmentSwitcher';
import VideoStorageService from '../../../services/VideoStorageService';
import './VideoFeed.scss';

/**
 * VideoFeed Component - TikTok-style video sharing interface
 * 
 * Features:
 * - Video upload with preview
 * - TikTok-style vertical video player
 * - Like, comment, and share functionality
 * - User profile integration
 * - Mobile-responsive design
 */
const VideoFeed = () => {
    const { authUser } = useContext(AuthUserContext);
    const [videos, setVideos] = useState([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadPreview, setUploadPreview] = useState(null);
    const [uploadCaption, setUploadCaption] = useState('');
    const [uploadTags, setUploadTags] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [uploadProgress, setUploadProgress] = useState({ step: '', progress: 0 });
    
    const videoRefs = useRef([]);
    const containerRef = useRef(null);

    // Sample video data structure - using working sample videos
    const sampleVideos = [
        {
            id: 1,
            url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
            thumbnail: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg',
            caption: 'Beautiful animated short film! 🎬 #animation #film #creative',
            tags: ['animation', 'film', 'creative'],
            user: {
                id: 'user1',
                username: 'filmmaker',
                avatar: 'https://i.pravatar.cc/150?img=1',
                displayName: 'Creative Filmmaker'
            },
            likes: 1234,
            comments: 89,
            shares: 45,
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            isLiked: false
        },
        {
            id: 2,
            url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
            thumbnail: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg',
            caption: 'Amazing 3D animation showcase! 🐘 #3d #animation #art',
            tags: ['3d', 'animation', 'art'],
            user: {
                id: 'user2',
                username: 'animator3d',
                avatar: 'https://i.pravatar.cc/150?img=2',
                displayName: '3D Artist'
            },
            likes: 856,
            comments: 67,
            shares: 23,
            timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
            isLiked: true
        }
    ];

    useEffect(() => {
        loadVideos();
    }, []);

    // Load videos from FHIR/IPFS storage
    const loadVideos = async () => {
        try {
            setIsLoading(true);
            
            // Try to load from FHIR first
            const fhirVideos = await VideoStorageService.fetchVideos(10, 0);
            
            if (fhirVideos.length > 0) {
                console.log('Loading videos from FHIR:', fhirVideos);
                setVideos(fhirVideos);
            } else {
                // Fallback to sample data if no FHIR videos
                console.log('No FHIR videos found, loading sample videos:', sampleVideos);
                setVideos(sampleVideos);
            }
            
            setIsLoading(false);
        } catch (error) {
            console.error('Error loading videos:', error);
            // Fallback to sample data on error
            setVideos(sampleVideos);
            setIsLoading(false);
        }
    };

    // Handle video file selection
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type.startsWith('video/')) {
                if (file.size > 100 * 1024 * 1024) { // 100MB limit
                    setUploadError('Video file must be less than 100MB');
                    return;
                }
                setUploadFile(file);
                setUploadError('');
                
                // Create preview URL
                const previewUrl = URL.createObjectURL(file);
                setUploadPreview(previewUrl);
            } else {
                setUploadError('Please select a video file');
            }
        }
    };

    // Handle video upload using VideoStorageService
    const handleUpload = async () => {
        if (!uploadFile || !authUser) {
            setUploadError('Please select a video file and ensure you are logged in');
            return;
        }

        try {
            setIsUploading(true);
            setUploadError('');
            setUploadProgress({ step: 'starting', progress: 0 });

            const metadata = {
                caption: uploadCaption || 'New video upload',
                tags: uploadTags ? uploadTags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
                title: uploadCaption || `Video by ${authUser.displayName || 'User'}`
            };

            // Upload using VideoStorageService (IPFS + FHIR)
            const uploadResult = await VideoStorageService.uploadVideo(
                uploadFile, 
                metadata, 
                authUser,
                (progress) => {
                    setUploadProgress(progress);
                }
            );

            // Create video object for immediate display
            const newVideo = {
                id: uploadResult.fhirId,
                url: uploadResult.url,
                thumbnailUrl: uploadResult.thumbnailUrl,
                caption: metadata.caption,
                tags: metadata.tags,
                user: {
                    id: authUser.uid,
                    username: authUser.displayName?.toLowerCase().replace(/\s+/g, '') || 'user',
                    avatar: authUser.photoURL || `https://i.pravatar.cc/150?u=${authUser.uid}`,
                    displayName: authUser.displayName || 'Anonymous User'
                },
                likes: 0,
                comments: 0,
                shares: 0,
                timestamp: new Date(),
                isLiked: false,
                size: uploadResult.size,
                ipfsHash: uploadResult.ipfsHash
            };

            setVideos(prev => [newVideo, ...prev]);
            setShowUploadModal(false);
            resetUploadForm();
            setIsUploading(false);
            setUploadProgress({ step: '', progress: 0 });

        } catch (error) {
            console.error('Error uploading video:', error);
            setUploadError(`Upload failed: ${error.message}`);
            setIsUploading(false);
            setUploadProgress({ step: '', progress: 0 });
        }
    };

    // Reset upload form
    const resetUploadForm = () => {
        setUploadFile(null);
        setUploadPreview(null);
        setUploadCaption('');
        setUploadTags('');
        setUploadError('');
        if (uploadPreview) {
            URL.revokeObjectURL(uploadPreview);
        }
    };

    // Handle like toggle with FHIR update
    const handleLike = async (videoId) => {
        try {
            // Optimistic update
            const video = videos.find(v => v.id === videoId);
            const wasLiked = video?.isLiked;
            
            setVideos(prev => prev.map(video => 
                video.id === videoId 
                    ? { 
                        ...video, 
                        isLiked: !video.isLiked,
                        likes: video.isLiked ? video.likes - 1 : video.likes + 1
                    }
                    : video
            ));

            // Update in FHIR
            const success = await VideoStorageService.updateVideoEngagement(
                videoId, 
                'likes', 
                !wasLiked
            );
            
            if (!success) {
                // Revert optimistic update on failure
                setVideos(prev => prev.map(video => 
                    video.id === videoId 
                        ? { 
                            ...video, 
                            isLiked: wasLiked,
                            likes: wasLiked ? video.likes + 1 : video.likes - 1
                        }
                        : video
                ));
            }
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };

    // Handle scroll to next video
    const handleScroll = () => {
        if (!containerRef.current) return;
        
        const container = containerRef.current;
        const scrollTop = container.scrollTop;
        const videoHeight = container.clientHeight;
        const newIndex = Math.round(scrollTop / videoHeight);
        
        if (newIndex !== currentVideoIndex && newIndex < videos.length) {
            setCurrentVideoIndex(newIndex);
            
            // Pause previous video and play current
            videoRefs.current.forEach((video, index) => {
                if (video) {
                    if (index === newIndex) {
                        video.play().catch(console.error);
                    } else {
                        video.pause();
                    }
                }
            });
        }
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        const now = new Date();
        const diff = now - timestamp;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        
        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        return 'Just now';
    };

    if (isLoading) {
        return (
            <CCard className="video-feed-card">
                <CCardBody className="text-center">
                    <CSpinner color="primary" />
                    <p className="mt-2">Loading videos...</p>
                </CCardBody>
            </CCard>
        );
    }

    return (
        <div className="video-feed-container">
            {/* Upload Button */}
            <div className="upload-section">
                <div className="upload-controls">
                    <CButton 
                        color="primary" 
                        onClick={() => setShowUploadModal(true)}
                        className="upload-btn"
                    >
                        📹 Share Video
                    </CButton>
                    <CButton 
                        color="outline-secondary" 
                        size="sm"
                        onClick={loadVideos}
                        disabled={isLoading}
                        className="refresh-btn"
                    >
                        {isLoading ? <CSpinner size="sm" /> : '🔄'}
                    </CButton>
                </div>
                <div className="video-count">
                    {videos.length} video{videos.length !== 1 ? 's' : ''} available
                </div>
            </div>

            {/* Video Feed */}
            <div 
                className="video-feed-scroll"
                ref={containerRef}
                onScroll={handleScroll}
            >
                {videos.length === 0 ? (
                    <div className="video-feed-empty">
                        <div className="empty-icon">📹</div>
                        <div className="empty-title">No Videos Yet</div>
                        <div className="empty-description">
                            Be the first to share a video! Click "Share Video" above to get started.
                        </div>
                    </div>
                ) : (
                    videos.map((video, index) => (
                        <div key={video.id} className="video-item">
                            <div className="video-container">
                                <video
                                    ref={el => videoRefs.current[index] = el}
                                    src={video.url}
                                    poster={video.thumbnail}
                                    loop
                                    muted
                                    playsInline
                                    className="video-player"
                                    onError={(e) => {
                                        console.error('Video failed to load:', video.url);
                                        e.target.style.display = 'none';
                                        // Show error placeholder
                                        const errorDiv = e.target.parentNode.querySelector('.video-error');
                                        if (errorDiv) errorDiv.style.display = 'flex';
                                    }}
                                    onLoadedData={() => {
                                        // Video loaded successfully
                                        const errorDiv = videoRefs.current[index]?.parentNode?.querySelector('.video-error');
                                        if (errorDiv) errorDiv.style.display = 'none';
                                    }}
                                    onClick={(e) => {
                                        if (e.target.paused) {
                                            e.target.play().catch(console.error);
                                        } else {
                                            e.target.pause();
                                        }
                                    }}
                                />
                                
                                {/* Video Error Placeholder */}
                                <div className="video-error" style={{ display: 'none' }}>
                                    <div className="error-content">
                                        <div className="error-icon">⚠️</div>
                                        <div className="error-text">Video unavailable</div>
                                        <div className="error-subtext">This video cannot be played</div>
                                    </div>
                                </div>
                            
                                {/* Video Overlay Info */}
                                <div className="video-overlay">
                                    <div className="video-info">
                                        <div className="user-info">
                                            <img 
                                                src={video.user.avatar} 
                                                alt={video.user.displayName}
                                                className="user-avatar"
                                            />
                                            <span className="username">@{video.user.username}</span>
                                            <span className="timestamp">{formatTimestamp(video.timestamp)}</span>
                                        </div>
                                        
                                        <div className="video-caption">
                                            {video.caption}
                                        </div>
                                        
                                        <div className="video-tags">
                                            {video.tags.map(tag => (
                                                <span key={tag} className="tag">#{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="video-actions">
                                        <button 
                                            className={`action-btn like-btn ${video.isLiked ? 'liked' : ''}`}
                                            onClick={() => handleLike(video.id)}
                                        >
                                            {video.isLiked ? '❤️' : '🤍'}
                                            <span>{video.likes}</span>
                                        </button>
                                        
                                        <button className="action-btn comment-btn">
                                            💬
                                            <span>{video.comments}</span>
                                        </button>
                                        
                                        <button className="action-btn share-btn">
                                            📤
                                            <span>{video.shares}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Upload Modal */}
            <CModal visible={showUploadModal} onClose={() => {
                setShowUploadModal(false);
                resetUploadForm();
            }}>
                <CModalHeader>
                    <CModalTitle>Share Your Video</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {uploadError && (
                        <CAlert color="danger">{uploadError}</CAlert>
                    )}
                    
                    {isUploading && (
                        <CAlert color="info">
                            <div className="upload-progress">
                                <div className="progress-info">
                                    <strong>Uploading: {uploadProgress.step.replace('_', ' ')}</strong>
                                    <span>{uploadProgress.progress}%</span>
                                </div>
                                <div className="progress-bar-container">
                                    <div 
                                        className="progress-bar" 
                                        style={{ width: `${uploadProgress.progress}%` }}
                                    ></div>
                                </div>
                                <small className="mb-2">
                                    {uploadProgress.step === 'compressing' && 'Optimizing video for web...'}
                                    {uploadProgress.step === 'thumbnail' && 'Generating thumbnail...'}
                                    {uploadProgress.step === 'uploading_video' && 'Uploading to IPFS...'}
                                    {uploadProgress.step === 'uploading_thumbnail' && 'Uploading thumbnail...'}
                                    {uploadProgress.step === 'saving_metadata' && 'Saving to FHIR...'}
                                    {uploadProgress.step === 'complete' && 'Upload complete!'}
                                </small>
                            </div>
                        </CAlert>
                    )}
                    
                    <div className="upload-form">
                        <div className="file-input-section">
                            <CFormInput
                                type="file"
                                accept="video/*"
                                onChange={handleFileSelect}
                                disabled={isUploading}
                            />
                            <small className="mb-2">
                                Supported formats: MP4, MOV, AVI (Max: 100MB)
                            </small>
                        </div>

                        {uploadPreview && (
                            <div className="upload-preview">
                                <video
                                    src={uploadPreview}
                                    controls
                                    className="preview-video"
                                    style={{ width: '100%', maxHeight: '200px' }}
                                />
                            </div>
                        )}

                        <div className="caption-section">
                            <CFormTextarea
                                placeholder="Write a caption..."
                                value={uploadCaption}
                                onChange={(e) => setUploadCaption(e.target.value)}
                                rows={3}
                                disabled={isUploading}
                            />
                        </div>

                        <div className="tags-section">
                            <CFormInput
                                placeholder="Add tags (comma separated): beach, sunset, nature"
                                value={uploadTags}
                                onChange={(e) => setUploadTags(e.target.value)}
                                disabled={isUploading}
                            />
                        </div>
                    </div>
                </CModalBody>
                <CModalFooter>
                    <CButton 
                        color="secondary" 
                        onClick={() => {
                            setShowUploadModal(false);
                            resetUploadForm();
                        }}
                        disabled={isUploading}
                    >
                        Cancel
                    </CButton>
                    <CButton 
                        color="primary" 
                        onClick={handleUpload}
                        disabled={!uploadFile || isUploading}
                    >
                        {isUploading ? (
                            <>
                                <CSpinner size="sm" className="me-2" />
                                Uploading...
                            </>
                        ) : (
                            'Share Video'
                        )}
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default VideoFeed;


